import { BILLING_MODELS } from '../../constants';
import { calculateOperationalCosts, getOperationalCostsDetails } from './operationalCosts';
import { linhasDeCongelamento } from './congelamento';
import { todasAsEtapasConcluidas } from '../../constants/schedule';

/**
 * Verifica se todas as etapas contratadas foram concluídas
 */
const checkAllContractedStagesDone = todasAsEtapasConcluidas;

/**
 * Calcula o valor do serviço baseado no modelo de cobrança.
 *
 * Valor não informado vale ZERO, não um preço de tabela. Havia um padrão por
 * modelo (R$ 500 a TE, R$ 1.200 a prenhez) que entrava em silêncio quando o
 * cadastro ficava sem valor, e o serviço aparecia faturável por um número que
 * ninguém combinou. Agora o cadastro exige o valor ao gravar, e o que sobrar
 * sem ele mostra zero — que é feio, e por isso mesmo vai ser visto.
 */
/**
 * O valor do serviço, ABERTO NAS PARTES QUE O FORMAM.
 *
 * Existe porque o relatório precisa responder "de onde vem cada real", e a
 * decomposição não pode ser calculada por fora: somar base, repasse e
 * congelamento num segundo lugar produziria um total que discorda deste — e o
 * dashboard mostraria as duas contas na mesma tela.
 *
 * `calculateServiceValue` é o total daqui. Uma conta, duas formas de ler.
 *
 * A BASE É O RESTO, e não uma quarta soma: `total − repasses − congelamento`. É
 * o que garante que as partes fechem com o total, hoje e depois de qualquer
 * mudança nas regras de preço. Se um dia entrar uma quinta parcela no valor, ela
 * aparece dentro da base até alguém abrir também — errado no rótulo, nunca no
 * total.
 */
export const composicaoDoValor = (service = {}) => {
  const vazio = { base: 0, repasses: [], congelamento: [], total: 0, fechou: false };

  // Sai antes dos custos operacionais de propósito. Serviço sem cobrança não
  // cobra NADA — nem o repasse de quilometragem ou material. Esses custos
  // acompanham o serviço onde o faturamento de fato acontece.
  if (service.billing_model === BILLING_MODELS.SEM_COBRANCA) return vazio;

  let baseValue = 0;
  // A etapa de que este modelo cobra já aconteceu?
  //
  // Não é o mesmo que "baseValue > 0": uma TE concluída com zero transferidos
  // fecha a conta em zero, e isso é um resultado, não uma espera. É essa
  // diferença que decide se os custos operacionais já podem ser cobrados.
  let fechou = false;

  switch (service.billing_model) {
    case BILLING_MODELS.POR_TE:
      fechou = !!service.te_done;
      if (service.te_done && service.te_quantity > 0) {
        const valuePerTE = parseFloat(service.value_per_te) || 0;
        baseValue = service.te_quantity * valuePerTE;
      }
      break;

    case BILLING_MODELS.POR_PRENHEZ:
      // Fatura baseado na quantidade de prenhezes aos 60D
      fechou = !!service.pregnancy_60d_done;
      if (service.pregnancy_60d_done && service.pregnancy_60d_quantity > 0) {
        const valuePerPregnancy = parseFloat(service.value_per_pregnancy) || 0;
        baseValue = service.pregnancy_60d_quantity * valuePerPregnancy;
      }
      break;

    case BILLING_MODELS.DIARIA:
      // A diaria corre desde o primeiro dia: nao ha etapa a esperar.
      fechou = true;
      const dailyValue = parseFloat(service.daily_value) || 0;
      const workDays = service.work_days || 1;
      baseValue = dailyValue * workDays;
      break;

    case BILLING_MODELS.PACOTE_COMPLETO:
    case BILLING_MODELS.PERSONALIZADO:
      const allDone = checkAllContractedStagesDone(service);
      fechou = allDone;
      if (allDone) {
        baseValue = parseFloat(service.package_value) || 0;
      }
      break;

    case BILLING_MODELS.POR_EMBRIAO:
      fechou = !!service.lab_done;
      if (service.lab_done && service.lab_quantity > 0) {
        const valuePerEmbryo = parseFloat(service.value_per_embryo) || 0;
        baseValue = service.lab_quantity * valuePerEmbryo;
      }
      break;

    default:
      break;
  }

  /**
   * Custo operacional acompanha a COBRANCA, nao existe sozinho.
   *
   * Ele era somado incondicionalmente, fora do switch. Um servico por prenhez
   * com a DG60 ainda por fazer tinha base zero e valor igual ao frete — e
   * `faturavelNaoFaturado` so pergunta se o valor e maior que zero. O servico
   * virava critico com "pode faturar e nao foi faturado", e o botao oferecia
   * cobrar o deslocamento sozinho, no meio do trabalho.
   *
   * `fechou` e a etapa de que o modelo cobra ter acontecido, nao o valor ter
   * dado positivo: TE concluida com zero transferidos fecha em zero, e ai o
   * repasse do frete e devido do mesmo jeito.
   */
  const operationalCosts = fechou ? calculateOperationalCosts(service) : 0;

  /**
   * Congelamento entra POR CIMA do modelo, nao no lugar dele: o servico e
   * cobrado por prenhez E os embrioes que sobraram estao no botijao. Fica
   * depois do `sem_cobranca` acima de proposito — servico sem cobranca nao
   * cobra nada, nem congelado.
   *
   * EXCETO em `por_embriao`, onde ele ja esta na base. Ali `lab_quantity` e o
   * PRODUZIDO, que por definicao soma o que foi para a TE mais o congelado em
   * DT, mais o vitrificado, mais o descartado. Somar o congelamento por cima
   * cobraria a mesma palheta duas vezes: uma como embriao produzido, outra
   * como embriao congelado. O docstring de congelamento.js lista "por prenhez
   * + vitrificado, por TE + vitrificado, pacote + vitrificado" e nao
   * considerou justamente o modelo em que o congelado ja esta contado.
   *
   * A desvitrificacao continua: e trabalho sobre embriao de outro laboratorio,
   * que nunca entrou em `lab_quantity`.
   */
  const linhasCobradas = linhasDeCongelamento(service).filter(l =>
    service.billing_model !== BILLING_MODELS.POR_EMBRIAO || l.tipo === 'desvitrificacao');
  const congelamento = linhasCobradas.reduce((s, l) => s + l.subtotal, 0);

  const total = baseValue + operationalCosts + congelamento;

  return {
    // O que o modelo de cobrança cobra: embrião transferido, prenhez, pacote,
    // diária, embrião produzido.
    base: total - operationalCosts - congelamento,
    // Cada repasse com o seu nome. O relatório mostra quilometragem separada de
    // material porque são decisões diferentes de quem negocia o contrato.
    repasses: operationalCosts > 0 ? getOperationalCostsDetails(service) : [],
    congelamento: linhasCobradas,
    total,
    fechou,
  };
};

export const calculateServiceValue = (service) => composicaoDoValor(service).total;
